import { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MetaAlias, Profile } from '@playcastdotio/Messaging';
import { invitationSendState, ownerAliasState, playerNameState, profileState } from '../../api/stores/lobbyStores';
import {
  queryProfile,
  updateProfile,
  queryProfileInvitations,
  createMatchmakerLobby,
  joinMatchmakerLobby,
  leaveMatchmakerLobby,
  startMatchmakerLobby,
  destroyMatchmakerLobby,
} from '../../api/modules/realtimeMessaging/handlers/profileMessage';
import { ProfileComponent, ProfileState } from '@playcastdotio/SharedComponents';
import { authStore, joinState, userEventsState } from '../../api/stores/globalStores';
import { connectPeer, startJoin } from '../../api/modules/player';
import { Player } from '../player/Player';
import { IdentityPlatform, InvitationSendState, PlayerJoinState } from '@playcastdotio/CoreTypes';
import { useClerk } from '@clerk/clerk-react';
import constants from '@playcastdotio/GlobalConfig';
import { getPersistentPlayerId } from '../../api';
import { kickPlayer } from '../../api/modules/realtimeMessaging/handlers/matchmakerMessage';
import { sendEmail, sendSms } from '../../api/modules/realtimeMessaging/handlers/outboundMessage';
import { sendDiscordBot } from '../../api/modules/fetch';
import { sendTwitchBot } from '../../api/modules/fetch';
interface ProfileProps {
  platform: IdentityPlatform;
}

export const ProfilePage = ({ platform }: ProfileProps) => {
  const navigate = useNavigate();
  let { alias } = useParams();
  const [state, setState] = useState<ProfileState | null>(null);
  const [sendState, setSendState] = useState<InvitationSendState>(invitationSendState.currentState);
  const [ownerAliases, setOwnerAliases] = useState<Partial<Record<IdentityPlatform, string>> | null>(null);
  const [ownerUsernames, setOwnerUsernames] = useState<Partial<Record<IdentityPlatform, string>> | null>(null);
  const [ownedLobbies, setOwnedLobbies] = useState<string[] | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [authId, setAuthId] = useState<string | null>(null);
  const [joinStatus, setJoinStatus] = useState<PlayerJoinState['state'] | null>(null);
  const lastOpenedMatchRef = useRef<string | null>(null);
  const lastOpenedBroadcastRef = useRef<string | null>(null);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const { openUserProfile } = useClerk();
  const [authToken, setAuthToken] = useState<string | null>(null);

  if (alias) {
    alias = alias.split('/')[0]; // Remove anything after a trailing slash
    alias = alias.split('?')[0]; // Just in case
  }

  useEffect(() => {
    const listener = authStore.addListener((state) => {
      setAuthToken(state.accessToken);
    });
    setAuthToken(authStore.currentState.accessToken);

    return () => {
      authStore.removeListener(listener);
    };
  }, []);

  useEffect(() => {
    console.log('Loading profile');

    const listener = authStore.addListener((state) => {
      setAuthId(state.playerAuthId);
    });
    setAuthId(authStore.currentState.playerAuthId);

    const foundListener = profileState.addListener((state) => {
      if (state.matchFoundId) {
        console.log('Match found', state.matchFoundId);
      }
      if (state.matchBroadcastId && state.matchFoundId) {
        // Only open a new window if this is a different match than the last one we opened
        if (lastOpenedMatchRef.current !== state.matchFoundId && lastOpenedBroadcastRef.current !== state.matchBroadcastId) {
          // Track this match ID as the last one we opened
          lastOpenedMatchRef.current = state.matchFoundId;
          lastOpenedBroadcastRef.current = state.matchBroadcastId;

          // Open link in new tab
          const playerLink = constants.PLAYER_BASE_URL;

          // Check for current URL parameters to pass through
          const currentParams = new URLSearchParams(window.location.search);
          let playerUrl = `${playerLink}j/${state.matchFoundId}`;

          // If we're in anonymous mode, pass the actual anonymous ID
          if (currentParams.has('anon')) {
            const currentPlayerId = getPersistentPlayerId();
            playerUrl += `?anon=${encodeURIComponent(currentPlayerId)}`;
          }
          // Pass through regular id parameter if present
          else if (currentParams.has('id')) {
            const idParam = currentParams.get('id');
            if (idParam) {
              playerUrl += `?id=${encodeURIComponent(idParam)}`;
            }
          }

          window.open(playerUrl, '_blank', 'noopener,noreferrer');
          console.log('Opened window for match', state.matchFoundId);
        } else {
          console.log('Window already opened for match', state.matchFoundId);
        }
      }
    });

    const invitationSendListener = invitationSendState.addListener((state) => {
      setSendState(state);
    });
    setSendState(invitationSendState.currentState);

    const ownerAliasesListener = ownerAliasState.addListener((state) => {
      setOwnerAliases(state.aliases);
      setOwnerUsernames(state.usernames);
    });
    setOwnerAliases(ownerAliasState.currentState.aliases);
    setOwnerUsernames(ownerAliasState.currentState.usernames);

    const ownedLobbiesListener = userEventsState.addListener((state) => {
      setOwnedLobbies(state.ownedLobbies ?? undefined);
    });
    setOwnedLobbies(userEventsState.currentState.ownedLobbies ?? undefined);

    return () => {
      authStore.removeListener(listener);
      profileState.removeListener(foundListener);
      ownerAliasState.removeListener(ownerAliasesListener);
      invitationSendState.removeListener(invitationSendListener);
      userEventsState.removeListener(ownedLobbiesListener);
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      console.log('Unloading profile');
    };
  }, []);

  // Useeffect for querying profile
  useEffect(() => {
    if (!alias) {
      profileState.updateState({
        ...profileState.currentState,
        error: 'No alias provided',
        isLoading: false,
      });
      return;
    }

    // Load profile data
    queryProfile(alias, platform);
  }, [alias, platform]);

  useEffect(() => {
    if (!alias) {
      profileState.updateState({
        ...profileState.currentState,
        error: 'No alias provided',
        isLoading: false,
      });
      return;
    }

    // Load initial invitations
    const cursor = currentPage > 0 ? state?.nextCursor : undefined;
    queryProfileInvitations(alias, platform, cursor, 16, searchTerm);

    // Set up refresh interval for invitations (every 30 seconds)
    refreshIntervalRef.current = setInterval(() => {
      console.log('Refreshing invitations data (30-second interval)');
      const cursor = currentPage > 0 ? state?.nextCursor : undefined;
      queryProfileInvitations(alias, platform, cursor, 16, searchTerm);
    }, 30 * 1000); // 30 seconds in milliseconds

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [alias, platform, searchTerm, currentPage]);

  useEffect(() => {
    const listener = profileState.addListener((state) => {
      setState(state);
    });
    return () => profileState.removeListener(listener);
  }, []);

  const onEdit = (profile: Profile) => {
    console.log('Editing profile', profile);
    updateProfile(profile);
  };

  const handlePageForward = () => {
    if (!alias || !state?.nextCursor) return;

    setCurrentPage(currentPage + 1);
    // queryProfileInvitations(alias, platform, state.nextCursor, 16, searchTerm);
  };

  const handlePageBack = () => {
    if (!alias || currentPage <= 0) return;

    // Reset to first page
    setCurrentPage(0);
    // queryProfileInvitations(alias, platform, undefined, 16, searchTerm);
  };

  const handleSearch = (term: string) => {
    if (!alias) return;

    setSearchTerm(term);
    setCurrentPage(0);
    // queryProfileInvitations(alias, platform, undefined, 16, term);
  };

  const handleCreateMatchmakerLobby = () => {
    console.log('Creating matchmaker lobby');
    profileState.updateState({
      ...profileState.currentState,
      creating: true,
    });
    createMatchmakerLobby();
  };

  const handleJoinMatchmakerLobby = (name?: string) => {
    if (name) {
      playerNameState.updateState({ name });
      localStorage.setItem('name', name);
    }
    console.log('Joining matchmaker lobby');
    joinMatchmakerLobby();
  };

  const handleLeaveMatchmakerLobby = () => {
    console.log('Leaving matchmaker lobby');
    leaveMatchmakerLobby();
  };

  const handleStartMatchmakerLobby = (useSteamGuestLogin?: boolean, regionOverride?: string) => {
    console.log('Starting matchmaker lobby', useSteamGuestLogin, regionOverride);
    startMatchmakerLobby(useSteamGuestLogin, regionOverride);
  };

  const handleDestroyMatchmakerLobby = (selectedInvitationId?: string) => {
    console.log('Destroying matchmaker lobby', selectedInvitationId);
    destroyMatchmakerLobby(selectedInvitationId);
  };

  const handleSelectInvitation = (invitationId?: string) => {
    console.log('Selecting invitation', invitationId);
    profileState.updateState({
      ...profileState.currentState,
      selectedInvitationId: invitationId,
    });
  };

  const handleSettingsCogClick = () => {
    console.log('Settings cog clicked');
    // Open clerk settings modal thingy
    openUserProfile();
  };

  const handleKickPlayer = (playerId: string) => {
    console.log('Kicking player', playerId);
    if (state?.selectedInvitationId) {
      kickPlayer(state.selectedInvitationId, playerId);
    }
  };

  const handleSendEmail = (invitationId: string, target: string, message: string) => {
    console.log('Sending email', invitationId, target, message);
    sendEmail(invitationId, target, message);
  };

  const handleSendSms = (invitationId: string, target: string, message: string) => {
    console.log('Sending SMS', invitationId, target, message);
    sendSms(invitationId, target, message);
  };

  const handleTwitchBot = (invitationId: string) => {
    console.log('Twitch bot', invitationId);
    sendTwitchBot(invitationId);
  };

  const handleDiscordBot = (invitationId: string) => {
    console.log('Discord bot', invitationId);
    sendDiscordBot(invitationId);
  };

  const handleRemoveSelectedInvitation = () => {
    console.log('Removing selected invitation');
    profileState.updateState({
      ...profileState.currentState,
      selectedInvitationId: undefined,
    });
  };

  const handleUpdatePlayerName = (name: string) => {
    console.log('Profile: handleUpdatePlayerName called with:', name);
    playerNameState.updateState({ name: name });
    console.log('Profile: Updated playerNameState with:', name);
    // Also save to localStorage for consistency
    localStorage.setItem('name', name);
    console.log('Profile: Saved to localStorage:', name);
  };

  if (joinStatus === 'hostConnected') {
    return <Player />;
  }

  return (
    <ProfileComponent
      state={state}
      authToken={authToken}
      onEdit={onEdit}
      alias={alias}
      ownerAliases={ownerAliases}
      ownerUsernames={ownerUsernames}
      ownedLobbies={ownedLobbies}
      invitations={state?.invitations || []}
      invitationsLoading={state?.invitationsLoading || false}
      invitationsError={state?.invitationsError || null}
      onSettingsCogClick={handleSettingsCogClick}
      onPageForward={handlePageForward}
      onPageBack={handlePageBack}
      currentPage={currentPage}
      hasNextPage={!!state?.nextCursor}
      onSearch={handleSearch}
      authId={authId || getPersistentPlayerId()}
      onCreateMatchmakerLobby={handleCreateMatchmakerLobby}
      onJoinMatchmakerLobby={handleJoinMatchmakerLobby}
      onLeaveMatchmakerLobby={handleLeaveMatchmakerLobby}
      onSelectInvitation={handleSelectInvitation}
      onStartMatchmakerLobby={handleStartMatchmakerLobby}
      onDestroyMatchmakerLobby={handleDestroyMatchmakerLobby}
      joinStatus={joinStatus}
      onKickPlayer={handleKickPlayer}
      invitationSendState={sendState}
      handleSendEmail={handleSendEmail}
      handleSendSms={handleSendSms}
      handleTwitchBot={handleTwitchBot}
      handleDiscordBot={handleDiscordBot}
      onRemoveSelectedInvitation={handleRemoveSelectedInvitation}
      onUpdatePlayerName={handleUpdatePlayerName}
      persistentId={getPersistentPlayerId()}
      parentType= 'browser'
    />
  );
};

